import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { userService } from './firestoreService';

/**
 * Risk Alert Manager
 * Monitors risk assessments and triggers alerts when thresholds are exceeded
 */
class RiskAlertManager {
  /**
   * Check if a risk assessment exceeds thresholds and trigger appropriate alerts
   */
  async checkRiskThresholds(assessment, context = {}) {
    const { finalRiskScore, riskLevel, baseRPN } = assessment;
    
    console.log('🔔 Checking risk thresholds:', { finalRiskScore, riskLevel });
    
    const alerts = [];
    
    // Critical alerts - Score >= 1000
    if (finalRiskScore >= 1000) {
      alerts.push(await this.triggerCriticalAlert(assessment, context));
    }
    // High priority alerts - Score >= 500
    else if (finalRiskScore >= 500) {
      alerts.push(await this.triggerHighPriorityAlert(assessment, context));
    }
    // Warning alerts - Score >= 200 (only if multipliers are significant)
    else if (finalRiskScore >= 200 && finalRiskScore > baseRPN * 1.5) {
      alerts.push(await this.triggerWarningAlert(assessment, context));
    }
    
    return alerts.filter(Boolean); // Remove nulls
  }
  
  /**
   * Trigger a critical alert - requires immediate action
   */
  async triggerCriticalAlert(assessment, context) {
    console.log('🚨 CRITICAL ALERT triggered for score:', assessment.finalRiskScore);
    
    const alert = {
      type: 'CRITICAL_RISK',
      severity: 'CRITICAL',
      title: `🚨 Critical Risk Detected`,
      message: this.buildAlertMessage(assessment, context),
      assessment: this.sanitizeAssessment(assessment),
      context: this.sanitizeContext(context),
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
      escalation: 'IMMEDIATE',
      status: 'ACTIVE',
      read: false,
      acknowledgedBy: [],
      recipientRoles: ['Management', 'IC Personnel', 'Administration'],
      recipientUsers: await this.getCriticalAlertRecipients(context),
      suggestedActions: [
        '🔴 Notify Infection Control Team immediately',
        '🔴 Activate enhanced isolation protocols',
        '🔴 Review patient placement and environment',
        '🔴 Contact hospital administration',
        '🔴 Document all actions taken'
      ],
      autoEscalation: {
        enabled: true,
        escalateAfterMinutes: 15,
        escalateToRoles: ['Management']
      }
    };
    
    return await this.saveAlert(alert);
  }
  
  /**
   * Trigger a high priority alert - requires prompt attention
   */
  async triggerHighPriorityAlert(assessment, context) {
    console.log('⚠️ HIGH PRIORITY alert triggered for score:', assessment.finalRiskScore);
    
    const alert = {
      type: 'HIGH_RISK',
      severity: 'HIGH',
      title: `⚠️ High Risk Detected`,
      message: this.buildAlertMessage(assessment, context),
      assessment: this.sanitizeAssessment(assessment),
      context: this.sanitizeContext(context),
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
      escalation: 'SUPERVISOR',
      status: 'ACTIVE',
      read: false,
      acknowledgedBy: [],
      recipientRoles: ['IC Personnel', 'Administration'],
      recipientUsers: await this.getHighPriorityAlertRecipients(context),
      suggestedActions: [
        '🟠 Review and apply enhanced protocols',
        '🟠 Notify department supervisor',
        '🟠 Assess environmental conditions',
        '🟠 Schedule risk review meeting',
        '🟠 Update risk mitigation plan'
      ],
      autoEscalation: {
        enabled: true,
        escalateAfterMinutes: 30,
        escalateToRoles: ['Management']
      }
    };
    
    return await this.saveAlert(alert);
  }
  
  /**
   * Trigger a warning alert - standard notification
   */
  async triggerWarningAlert(assessment, context) {
    console.log('⚡ WARNING alert triggered for score:', assessment.finalRiskScore);
    
    const alert = {
      type: 'RISK_WARNING',
      severity: 'MEDIUM',
      title: `⚡ Risk Warning`,
      message: this.buildAlertMessage(assessment, context),
      assessment: this.sanitizeAssessment(assessment),
      context: this.sanitizeContext(context),
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
      escalation: 'STANDARD',
      status: 'ACTIVE',
      read: false,
      acknowledgedBy: [],
      recipientRoles: ['IC Personnel', 'Execution'],
      recipientUsers: await this.getWarningAlertRecipients(context),
      suggestedActions: [
        '🟡 Review current protocols and compliance',
        '🟡 Monitor risk factors closely',
        '🟡 Update risk assessment documentation',
        '🟡 Brief staff on precautions',
        '🟡 Ensure adequate PPE availability'
      ],
      autoEscalation: {
        enabled: false
      }
    };
    
    return await this.saveAlert(alert);
  }
  
  /**
   * Build a detailed alert message
   */
  buildAlertMessage(assessment, context) {
    const parts = [];
    
    // Basic risk info
    parts.push(`Risk Score: ${Math.round(assessment.finalRiskScore)} (${assessment.riskLevel})`);
    parts.push(`Base RPN: ${assessment.baseRPN}`);
    
    // Multiplier breakdown
    if (assessment.multipliers) {
      const m = assessment.multipliers;
      if (m.location > 1.0) {
        parts.push(`📍 Location multiplier: ×${m.location.toFixed(2)}`);
      }
      if (m.patient > 1.0) {
        parts.push(`👤 Patient multiplier: ×${m.patient.toFixed(2)}`);
      }
      if (m.environmental > 1.0) {
        parts.push(`🌍 Environmental multiplier: ×${m.environmental.toFixed(2)}`);
      }
      if (m.biological > 1.0) {
        parts.push(`🦠 Biological multiplier: ×${m.biological.toFixed(2)}`);
      }
    }
    
    // Context information
    if (context.patientName) {
      parts.push(`Patient: ${context.patientName}`);
    }
    if (context.departmentName) {
      parts.push(`Department: ${context.departmentName}`);
    }
    if (context.taskName) {
      parts.push(`Task: ${context.taskName}`);
    }
    
    return parts.join(' | ');
  }
  
  /**
   * Sanitize assessment data for storage (remove functions, circular refs, undefined)
   */
  sanitizeAssessment(assessment) {
    const raw = {
      baseRPN: assessment?.baseRPN,
      finalRiskScore: assessment?.finalRiskScore,
      riskLevel: assessment?.riskLevel,
      multipliers: assessment?.multipliers,
      recommendations: assessment?.recommendations
    };
    return Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined));
  }
  
  /**
   * Sanitize context data for storage (omit undefined - Firestore rejects undefined)
   */
  sanitizeContext(context) {
    const raw = {
      patientId: context?.patientId,
      patientName: context?.patientName,
      departmentId: context?.departmentId,
      departmentName: context?.departmentName,
      roomId: context?.roomId,
      roomName: context?.roomName,
      taskId: context?.taskId,
      taskName: context?.taskName,
      userId: context?.userId,
      userName: context?.userName,
      protocolId: context?.protocolId,
      workflowExecutionId: context?.workflowExecutionId
    };
    return Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined));
  }
  
  /**
   * Get recipients for critical alerts
   */
  async getCriticalAlertRecipients(context) {
    try {
      const users = await userService.getAll();
      
      // All Management, IC Personnel, and department admins
      const recipients = users.filter(user => 
        user.role === 'Management' || 
        user.role === 'IC Personnel' ||
        (user.role === 'Administration' && user.primary_department_id === context.departmentId)
      );
      
      return recipients.map(u => u.uid);
    } catch (error) {
      console.error('Error getting critical alert recipients:', error);
      return [];
    }
  }
  
  /**
   * Get recipients for high priority alerts
   */
  async getHighPriorityAlertRecipients(context) {
    try {
      const users = await userService.getAll();
      
      // IC Personnel and department staff
      const recipients = users.filter(user => 
        user.role === 'IC Personnel' ||
        user.primary_department_id === context.departmentId
      );
      
      return recipients.map(u => u.uid);
    } catch (error) {
      console.error('Error getting high priority alert recipients:', error);
      return [];
    }
  }
  
  /**
   * Get recipients for warning alerts
   */
  async getWarningAlertRecipients(context) {
    try {
      const users = await userService.getAll();
      
      // Department staff only
      const recipients = users.filter(user => 
        user.primary_department_id === context.departmentId
      );
      
      return recipients.map(u => u.uid);
    } catch (error) {
      console.error('Error getting warning alert recipients:', error);
      return [];
    }
  }
  
  /**
   * Omit undefined values (Firestore rejects undefined)
   */
  omitUndefined(obj) {
    if (obj == null || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj;
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v === undefined) continue;
      out[k] = (typeof v === 'object' && v !== null && v.constructor === Object) ? this.omitUndefined(v) : v;
    }
    return out;
  }

  /**
   * Save alert to Firestore
   */
  async saveAlert(alert) {
    try {
      const alertsRef = collection(db, 'risk_alerts');
      const payload = this.omitUndefined(alert);
      const docRef = await addDoc(alertsRef, payload);
      
      console.log('✅ Alert saved:', docRef.id);
      
      return {
        id: docRef.id,
        ...alert
      };
    } catch (error) {
      console.error('❌ Error saving alert:', error);
      throw error;
    }
  }
  
  /**
   * Log alert to console for debugging
   */
  logAlert(alert) {
    console.log('🔔 ALERT TRIGGERED:', {
      severity: alert.severity,
      title: alert.title,
      message: alert.message,
      recipients: alert.recipientRoles
    });
  }
}

// Export singleton instance
export const riskAlertManager = new RiskAlertManager();
export default riskAlertManager;

